import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Apple,
  Bot,
  Check,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  LockOpen,
  Loader2,
  Play,
} from "lucide-react";
import { toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { buildAppBundle, type AppBundlePlatform } from "@/app-bridge/functions/app-bundle";
import { getAppBundleLockStatus } from "@/app-bridge/functions/app-bundle.functions";

export const Route = createFileRoute("/_authenticated/admin/appunderlag")({
  head: () => ({
    meta: [
      { title: "Appunderlag — Sellavo admin" },
      {
        name: "description",
        content: "Allt som appskalet hämtar för iOS och Android: ikoner, färger, behörigheter och texter.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AppBundlePage,
});

const PLATFORMS: { value: AppBundlePlatform; label: string; icon: typeof Apple }[] = [
  { value: "ios", label: "iOS", icon: Apple },
  { value: "android", label: "Android", icon: Bot },
];

function AppBundlePage() {
  const [platform, setPlatform] = useState<AppBundlePlatform>("ios");
  const lockFn = useServerFn(getAppBundleLockStatus);
  const lock = useQuery({ queryKey: ["app-bundle-lock"], queryFn: () => lockFn({}) });

  return (
    <AdminShell title="Appunderlag">
      <p className="mb-4 text-sm text-muted-foreground">
        Exakt det som appskalet hämtar från våra öppna adresser. Ändras något här i koden slår det
        igenom både i appen och på butiksunderlaget.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-3 text-sm">
        {lock.data?.locked ? (
          <>
            <Lock className="h-4 w-4 text-primary" aria-hidden="true" />
            <span className="font-medium">Nyckellåset är på.</span>
            <span className="text-muted-foreground">
              Anropen måste skicka headern <code className="rounded bg-muted px-1">X-App-Key</code>.
            </span>
          </>
        ) : (
          <>
            <LockOpen className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="font-medium">Adresserna är öppna.</span>
            <span className="text-muted-foreground">
              Innehållet är offentligt. Sätt hemligheten <code className="rounded bg-muted px-1">APP_BUNDLE_KEY</code>{" "}
              för att kräva en nyckel. Takt-spärr: 60 anrop per minut och IP.
            </span>
          </>
        )}
      </div>

      <AppBundleKeyCard lockData={lock.data} />

      <Tabs value={platform} onValueChange={(v) => setPlatform(v as AppBundlePlatform)}>
        <TabsList className="mb-4">
          {PLATFORMS.map((p) => (
            <TabsTrigger key={p.value} value={p.value} className="gap-2">
              <p.icon className="h-4 w-4" aria-hidden="true" />
              {p.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {PLATFORMS.map((p) => (
          <TabsContent key={p.value} value={p.value} className="space-y-4">
            <PlatformPanel platform={p.value} />
          </TabsContent>
        ))}
      </Tabs>
    </AdminShell>
  );
}

type LockStatus = Awaited<ReturnType<typeof getAppBundleLockStatus>>;

function AppBundleKeyCard({ lockData }: { lockData: LockStatus | undefined }) {
  const [revealed, setRevealed] = useState(false);

  if (!lockData) return null;

  const { configured, canReveal, key } = lockData;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-primary" aria-hidden="true" />
          APP_BUNDLE_KEY
        </CardTitle>
        <CardDescription>
          Nyckeln som appskalet skickar i headern <code className="rounded bg-muted px-1">X-App-Key</code> för att
          hämta app-underlagen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!configured ? (
          <p className="text-sm text-muted-foreground">
            Sätt hemligheten <code className="rounded bg-muted px-1">APP_BUNDLE_KEY</code> för att skydda
            app-underlagen.
          </p>
        ) : !canReveal ? (
          <p className="text-sm text-muted-foreground">
            En nyckel är konfigurerad, men du saknar behörighet att visa den.
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
              {revealed ? key : "•".repeat(24)}
            </code>
            <Button size="sm" variant="outline" onClick={() => setRevealed((v) => !v)}>
              {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {revealed ? "Dölj nyckel" : "Visa nyckel"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!revealed}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(key);
                  toast.success("Nyckeln är kopierad.");
                } catch {
                  toast.error("Kunde inte kopiera nyckeln.");
                }
              }}
            >
              <Copy className="h-4 w-4" /> Kopiera
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlatformPanel({ platform }: { platform: AppBundlePlatform }) {
  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const bundle = useMemo(() => buildAppBundle(platform, origin), [platform, origin]);
  const url = `${origin}/api/public/app/${platform}`;

  return (
    <>
      <Section title="Adressen wrappern anropar">
        <div className="flex flex-wrap items-center gap-2">
          <code className="min-w-0 flex-1 truncate rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs">
            {url}
          </code>
          <CopyButton text={url} label="Kopiera" />
          <a href={url} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline">
              <ExternalLink className="h-4 w-4" /> Öppna
            </Button>
          </a>
          <EndpointTest url={url} />
        </div>
      </Section>

      <Section title="Identitet">
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {Object.entries(bundle.identity).map(([key, value]) => (
            <div key={key} className="flex justify-between gap-3 border-b border-border/50 py-1">
              <dt className="text-muted-foreground">{IDENTITY_LABELS[key] ?? key}</dt>
              <dd className="text-right font-medium">{String(value)}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title={`Ikoner (${bundle.icons.length} st)`}>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {bundle.icons.map((icon) => (
            <figure
              key={icon.src}
              className="flex flex-col items-center gap-2 rounded-lg border border-border bg-muted/30 p-3 text-center"
            >
              <img
                src={icon.src}
                alt={`Ikon ${icon.size} pixlar`}
                width={56}
                height={56}
                loading="lazy"
                className="h-14 w-14 rounded-xl object-contain"
              />
              <figcaption className="text-xs">
                <span className="block font-semibold">
                  {icon.size}×{icon.size}
                </span>
                <span className="block text-muted-foreground">{icon.use}</span>
                {icon.opaque && <span className="mt-1 block text-[10px] text-primary">Utan genomskinlighet</span>}
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      <Section title="Startskärm och färger">
        <div className="flex flex-wrap gap-4">
          {[
            { label: "Ljus startskärm", src: bundle.splash.light },
            { label: "Mörk startskärm", src: bundle.splash.dark },
          ].map((s) => (
            <figure key={s.src} className="text-center text-xs">
              <img
                src={s.src}
                alt={s.label}
                loading="lazy"
                className="h-40 w-auto rounded-lg border border-border object-contain"
              />
              <figcaption className="mt-1 text-muted-foreground">{s.label}</figcaption>
            </figure>
          ))}
          <div className="flex flex-wrap items-start gap-3">
            {[
              { label: "Temafärg", hex: bundle.appearance.themeColor },
              { label: "Bakgrund ljus", hex: bundle.splash.backgroundLight },
              { label: "Bakgrund mörk", hex: bundle.splash.backgroundDark },
            ].map((c) => (
              <div key={c.label} className="text-center text-xs">
                <span
                  className="block h-14 w-14 rounded-lg border border-border"
                  style={{ backgroundColor: c.hex }}
                  aria-hidden="true"
                />
                <span className="mt-1 block font-medium">{c.label}</span>
                <span className="block text-muted-foreground">{c.hex}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          Skärmläge: {bundle.appearance.orientation} · Statusfält: {bundle.appearance.statusBarStyle} · Mörkt läge
          stöds: {bundle.appearance.supportsDarkMode ? "ja" : "nej"}
        </p>
      </Section>

      <Section title="Behörigheter">
        <ul className="space-y-2 text-sm">
          {bundle.permissions.map((perm) => (
            <li key={perm.key} className="rounded-lg border border-border bg-muted/30 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs font-semibold">{perm.key}</code>
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    perm.optional ? "bg-muted text-muted-foreground" : "bg-primary/10 text-primary"
                  }`}
                >
                  {perm.optional ? "Frivillig" : "Krävs"}
                </span>
              </div>
              <p className="mt-1 text-muted-foreground">{perm.purpose}</p>
              {perm.text && <p className="mt-1 italic text-muted-foreground">”{perm.text}”</p>}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Funktioner appen ska erbjuda">
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {bundle.capabilities.map((cap) => (
            <li key={cap.key} className="flex items-start justify-between gap-3 rounded-lg border border-border p-2">
              <span>
                <span className="font-medium">{cap.label}</span>
                <code className="mt-0.5 block text-[11px] text-muted-foreground">{cap.api}</code>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{cap.required ? "Krävs" : "Frivillig"}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Länkar">
        <dl className="space-y-1 text-sm">
          {Object.entries(bundle.links).map(([key, value]) => (
            <div key={key} className="flex flex-wrap justify-between gap-2 border-b border-border/50 py-1">
              <dt className="text-muted-foreground">{LINK_LABELS[key] ?? key}</dt>
              <dd className="max-w-full truncate text-right font-medium">
                {Array.isArray(value) ? value.join(", ") : String(value)}
              </dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section title={platform === "ios" ? "iOS-inställningar" : "Android-inställningar"}>
        <dl className="space-y-1 text-sm">
          {Object.entries((platform === "ios" ? bundle.ios : bundle.android) ?? {}).map(([key, value]) => (
            <div key={key} className="flex flex-wrap justify-between gap-2 border-b border-border/50 py-1">
              <dt className="text-muted-foreground">{key}</dt>
              <dd className="max-w-full break-all text-right font-medium">
                {typeof value === "object" && value !== null
                  ? Object.entries(value as Record<string, unknown>)
                      .map(([k, v]) => `${k}: ${String(v)}`)
                      .join(" · ")
                  : Array.isArray(value)
                    ? (value as string[]).join(", ") || "—"
                    : String(value)}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-xs text-muted-foreground">
          Team ID (Apple) och SHA-256 (Play App Signing) fylls i i <code>src/app-bridge/functions/app-bundle.ts</code>{" "}
          – tills dess svarar verifieringsfilerna med platshållare.
        </p>
      </Section>


      <Section title="Butikstexter">
        <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
          {`Namn: ${bundle.store.name}
Undertitel: ${bundle.store.subtitle}
Kort: ${bundle.store.shortDescription}
Nyckelord: ${bundle.store.keywords.join(", ")}

${bundle.store.description}

${bundle.store.reviewNotes}`}
        </pre>
      </Section>

      <Section title="Råsvar">
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground">Visa hela JSON-svaret</summary>
          <div className="mt-2">
            <CopyButton text={JSON.stringify(bundle, null, 2)} label="Kopiera JSON" />
            <pre className="mt-2 max-h-96 overflow-auto rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-muted-foreground">
              {JSON.stringify(bundle, null, 2)}
            </pre>
          </div>
        </details>
      </Section>
    </>
  );
}

const IDENTITY_LABELS: Record<string, string> = {
  name: "Namn",
  shortName: "Kort namn",
  bundleId: "Bundle-id / paket",
  description: "Beskrivning",
  language: "Språk",
  category: "Kategori",
  ageRating: "Åldersgräns",
  vendor: "Utgivare",
};

const LINK_LABELS: Record<string, string> = {
  startUrl: "Startadress",
  allowedHosts: "Tillåtna domäner",
  deepLinkScheme: "Djuplänkschema",
  universalLinkPaths: "Djuplänkar",
  privacyPolicy: "Integritetspolicy",
  terms: "Villkor",
  support: "Support",
  deleteAccount: "Radera konto",
  supportEmail: "Supportmejl",
  manifest: "Manifest",
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <h2 className="mb-3 font-heading text-base font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        } catch {
          /* ignoreras */
        }
      }}
    >
      {done ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      {done ? "Kopierat" : label}
    </Button>
  );
}

/** Hämtar den skarpa adressen och visar status, svarstid och version. */
function EndpointTest({ url }: { url: string }) {
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setResult(null);
    const started = Date.now();
    try {
      const res = await fetch(url, { cache: "no-store" });
      const ms = Date.now() - started;
      if (!res.ok) {
        setResult(`Fel ${res.status} efter ${ms} ms`);
        return;
      }
      const data = (await res.json()) as { version?: string; icons?: unknown[] };
      setResult(`OK · ${ms} ms · version ${data.version ?? "?"} · ${data.icons?.length ?? 0} ikoner`);
    } catch {
      setResult("Kunde inte nå adressen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => void run()} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
        Testa adressen
      </Button>
      {result && <span className="text-xs text-muted-foreground">{result}</span>}
    </>
  );
}
